"use client";

import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from "react";

/* Phase 4.4 — anchored floating panels that escape their scroll container.
 *
 * Tooltip and Popover both positioned themselves with plain CSS offsets on an
 * absolutely-positioned child of the trigger. That works on an open page and
 * fails everywhere it matters in this app: `.rc-datatable-wrap` is
 * `overflow: auto` (it has to be — these tables are wider than the shell), and
 * an overflow container clips its absolutely-positioned descendants. So every
 * tooltip on a table row action and every popover in a toolbar was cut off at
 * the container edge, which is finding F15's second half.
 *
 * The fix is `position: fixed` coordinates measured off the trigger's own
 * viewport rect, with the panel portalled to <body>. Nothing between the
 * trigger and the panel can clip it, because there is no longer anything
 * between them.
 *
 * This is a deliberately small hand-rolled positioner rather than a
 * floating-ui dependency: this app needs four sides, one flip and one clamp,
 * and that is ~60 lines. It does NOT do arrow positioning, virtual elements,
 * or middleware chains. If those are ever needed, replace it wholesale rather
 * than growing it.
 */

export type Side = "top" | "bottom" | "left" | "right";
export type Align = "start" | "center" | "end";

export interface AnchoredPosition {
  x: number;
  y: number;
  /** The side actually used, after flipping. Drive the arrow/animation off this. */
  side: Side;
  /** False until the first real measurement, so the panel can stay hidden. */
  ready: boolean;
}

const VIEWPORT_PADDING = 8;

function flipSide(side: Side): Side {
  return side === "top" ? "bottom" : side === "bottom" ? "top" : side === "left" ? "right" : "left";
}

function fits(side: Side, anchor: DOMRect, panel: { width: number; height: number }, offset: number): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  switch (side) {
    case "top": return anchor.top - panel.height - offset >= VIEWPORT_PADDING;
    case "bottom": return anchor.bottom + panel.height + offset <= vh - VIEWPORT_PADDING;
    case "left": return anchor.left - panel.width - offset >= VIEWPORT_PADDING;
    case "right": return anchor.right + panel.width + offset <= vw - VIEWPORT_PADDING;
  }
}

export interface UseAnchoredPositionOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  side?: Side;
  align?: Align;
  /** Gap between trigger and panel, in px. */
  offset?: number;
  /** Stretch the panel to at least the trigger's width (select-style menus). */
  matchTriggerWidth?: boolean;
}

export function useAnchoredPosition({
  open,
  anchorRef,
  panelRef,
  side = "bottom",
  align = "start",
  offset = 6,
  matchTriggerWidth = false,
}: UseAnchoredPositionOptions): AnchoredPosition & { minWidth?: number } {
  const [pos, setPos] = useState<AnchoredPosition & { minWidth?: number }>({
    x: 0, y: 0, side, ready: false,
  });

  const measure = useCallback(() => {
    const anchorEl = anchorRef.current;
    const panelEl = panelRef.current;
    if (!anchorEl || !panelEl) return;

    const a = anchorEl.getBoundingClientRect();
    const p = { width: panelEl.offsetWidth, height: panelEl.offsetHeight };

    // One flip, not a search: if the requested side does not fit and its
    // opposite does, use the opposite. If neither fits the clamp below keeps
    // it on screen, which is the honest outcome for a panel taller than the
    // viewport.
    let placed: Side = side;
    if (!fits(side, a, p, offset) && fits(flipSide(side), a, p, offset)) placed = flipSide(side);

    let x: number;
    let y: number;
    if (placed === "top" || placed === "bottom") {
      y = placed === "top" ? a.top - p.height - offset : a.bottom + offset;
      x = align === "start" ? a.left : align === "end" ? a.right - p.width : a.left + a.width / 2 - p.width / 2;
    } else {
      x = placed === "left" ? a.left - p.width - offset : a.right + offset;
      y = align === "start" ? a.top : align === "end" ? a.bottom - p.height : a.top + a.height / 2 - p.height / 2;
    }

    // Clamp into the viewport so a panel anchored near an edge is never
    // half-off-screen — the common case for row actions in the last column.
    x = Math.min(Math.max(VIEWPORT_PADDING, x), Math.max(VIEWPORT_PADDING, window.innerWidth - p.width - VIEWPORT_PADDING));
    y = Math.min(Math.max(VIEWPORT_PADDING, y), Math.max(VIEWPORT_PADDING, window.innerHeight - p.height - VIEWPORT_PADDING));

    setPos({ x, y, side: placed, ready: true, ...(matchTriggerWidth ? { minWidth: a.width } : {}) });
  }, [anchorRef, panelRef, side, align, offset, matchTriggerWidth]);

  // Layout effect so the first paint already has real coordinates — a plain
  // useEffect here shows the panel at 0,0 for one frame, which reads as a
  // flash in the top-left corner.
  //
  // No reset when `open` goes false: the consumer unmounts the panel
  // entirely, so there is nothing to hide, and on the next open this same
  // layout effect re-measures synchronously before the browser paints. The
  // stale coordinates therefore never reach a frame.
  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    // `true` for scroll so ancestor scroll containers (the table wrapper, the
    // page shell) are caught too, not just the window.
    const onScroll = () => measure();
    const onResize = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    // The panel's own size can change after mount (async content, a filtered
    // combobox list shrinking), which moves where it should sit.
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && panelRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(panelRef.current);
    }
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [open, measure, panelRef]);

  return pos;
}
